import type { NotificationBatchActionCatalogEntry } from "@kontextwerk/control-web-frame/components/notifications/NotificationsTable.types"
import {
  mdiAccountCheckOutline,
  mdiAccountSwitchOutline,
  mdiCalendarCheckOutline,
  mdiCardAccountDetailsOutline,
  mdiDownload,
} from "@mdi/js"
import {
  INBOX_BATCH_ACTION_TOOLTIP_CLASS,
  inboxBatchActionTooltip,
  inboxBatchActionTooltipWithDisabledReason,
} from "./inboxBatchActionTooltips"

type InboxBatchActionCatalogEntryInput = Omit<
  NotificationBatchActionCatalogEntry,
  "tooltipClassName" | "tooltipContent" | "disabledTooltipContent"
> & {
  tooltipContent: string
  disabledReason: string
}

const catalogEntry = ({
  tooltipContent,
  disabledReason,
  ...entry
}: InboxBatchActionCatalogEntryInput): NotificationBatchActionCatalogEntry => ({
  ...entry,
  tooltipContent,
  disabledTooltipContent: inboxBatchActionTooltipWithDisabledReason(tooltipContent, disabledReason),
  tooltipClassName: INBOX_BATCH_ACTION_TOOLTIP_CLASS,
})

export const KEHRBLATT_INBOX_BATCH_ACTION_CATALOG: NotificationBatchActionCatalogEntry[] = [
  catalogEntry({
    id: "kehrblatt.inbox-case.customer_contact_verification",
    batchLabel: "Ansprechpartner zuordnen",
    batchGroup: "Ansprechpartner",
    batchOrder: 10,
    iconPath: mdiAccountCheckOutline,
    tooltipContent: inboxBatchActionTooltip({
      title: "Ansprechpartner zuordnen",
      action:
        "Öffnet die ausgewählten To-dos, in denen ein gemeldeter Ansprechpartner zur Liegenschaft oder Einheit eingeordnet werden muss.",
      effect:
        "Sie entscheiden im Workflow, ob der Ansprechpartner neu angelegt, einem bestehenden Ansprechpartner zugeordnet oder nicht übernommen wird.",
      prerequisite: "Die Mitteilung ist ein offenes To-do für einen gemeldeten Ansprechpartner.",
    }),
    disabledReason: "Keine markierte Zeile ist ein offenes To-do für gemeldete Ansprechpartner.",
  }),
  catalogEntry({
    id: "kehrblatt.inbox-case.customer_contact_point_review",
    batchLabel: "Kontaktdaten prüfen",
    batchGroup: "Ansprechpartner",
    batchOrder: 20,
    iconPath: mdiCardAccountDetailsOutline,
    tooltipContent: inboxBatchActionTooltip({
      title: "Kontaktdaten prüfen",
      action:
        "Öffnet die ausgewählten To-dos mit neuen, geänderten oder zur Löschung angefragten Kontaktdaten.",
      effect:
        "Sie entscheiden je Kontaktwert, ob KehrBlatt ihn zusätzlich speichert, einen alten Wert ersetzt, löscht oder beibehält.",
      prerequisite: "Die Mitteilung ist ein offenes To-do mit zu prüfenden Kontaktdaten.",
    }),
    disabledReason: "Keine markierte Zeile ist ein offenes To-do mit zu prüfenden Kontaktdaten.",
  }),
  catalogEntry({
    id: "kehrblatt.inbox-case.wrong_recipient_replacement",
    batchLabel: "Ansprechpartner austauschen",
    batchGroup: "Ansprechpartner",
    batchOrder: 30,
    iconPath: mdiAccountSwitchOutline,
    tooltipContent: inboxBatchActionTooltip({
      title: "Ansprechpartner austauschen",
      action:
        "Öffnet die ausgewählten To-dos, in denen ein bisheriger Ansprechpartner nicht zuständig ist.",
      effect:
        "Sie ersetzen die falsche Zuordnung durch den gemeldeten richtigen Ansprechpartner oder entfernen sie ohne Ersatz.",
      prerequisite: "Die Mitteilung ist ein offenes To-do mit falschem Ansprechpartner.",
    }),
    disabledReason: "Keine markierte Zeile ist ein offenes To-do mit falschem Ansprechpartner.",
  }),
  catalogEntry({
    id: "kehrblatt.inbox-case.unclear_appointment_feedback",
    batchLabel: "Terminrücklauf klären",
    batchGroup: "Termine",
    batchOrder: 20,
    iconPath: mdiCalendarCheckOutline,
    tooltipContent: inboxBatchActionTooltip({
      title: "Terminrücklauf klären",
      action: "Öffnet unklare Terminvereinbarungen, Terminverschiebungen und Terminabsagen.",
      effect:
        "Sie klären Auftrag, Ansprechpartner und Termin und bereiten danach die Kundeninformation vor.",
      prerequisite: "Die Mitteilung ist ein offener unklarer Terminrücklauf.",
    }),
    disabledReason: "Keine markierte Zeile ist ein offener unklarer Terminrücklauf.",
  }),
  catalogEntry({
    id: "kehrblatt.inbox-case.letter_delivery_batch",
    batchLabel: "Terminbriefe zustellen",
    batchGroup: "Termine",
    batchOrder: 10,
    iconPath: mdiDownload,
    tooltipContent: inboxBatchActionTooltip({
      title: "Terminbriefe zustellen",
      action:
        "Öffnet To-dos mit fälligen Terminbriefen, die der Betrieb drucken und zustellen muss.",
      effect:
        "Sie legen die Zustellart fest, können händische Briefe als PDF herunterladen und schließen die ausgewählten Briefe über Weiter ab.",
      prerequisite: "Die Mitteilung ist ein offenes Terminbrief-Zustell-To-do.",
    }),
    disabledReason: "Keine markierte Zeile ist ein offenes Terminbrief-Zustell-To-do.",
  }),
]
